import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  RefreshCw,
  ExternalLink,
  Play,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';
import { useIntegrationsStore } from '../stores/integrations.store';
import { integrationsApi, Integration } from '../services/integrations.service';
import { fetchServiceAccountEmail, fetchCalendarEvents } from '../services/google-calendar.service';
import { fetchSheetNames } from '../services/google-sheets.service';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';

// ─── Integration type labels ──────────────────────────────────────────────────
const TYPE_LABELS: Record<string, string> = {
  google_sheets: 'Google Sheets',
  google_calendar: 'Google Calendar',
  rest_api: 'API REST',
};

const TYPE_ICONS: Record<string, string> = {
  google_sheets: '📊',
  google_calendar: '📅',
  rest_api: '🔌',
};

// ─── Create / Edit modal ──────────────────────────────────────────────────────
interface IntegrationFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; type: string; config: Record<string, any> }) => void;
  initial?: Integration | null;
  serviceAccountEmail: string | null;
  isSuperAdmin: boolean;
}

function IntegrationFormModal({
  open,
  onClose,
  onSave,
  initial,
  serviceAccountEmail,
  isSuperAdmin,
}: IntegrationFormProps) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<'google_sheets' | 'google_calendar' | 'rest_api'>(
    (initial?.type as any) ?? 'google_sheets',
  );
  const [spreadsheetId, setSpreadsheetId] = useState<string>(
    (initial?.config?.spreadsheetId as string) ?? '',
  );
  const [calendarId, setCalendarId] = useState<string>(
    (initial?.config?.calendarId as string) ?? 'primary',
  );
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [sheetOk, setSheetOk] = useState(false);
  // REST API state
  const [apiUrl, setApiUrl] = useState<string>((initial?.config?.url as string) ?? '');
  const [apiMethod, setApiMethod] = useState<string>((initial?.config?.method as string) ?? 'GET');
  const [apiAuthType, setApiAuthType] = useState<string>((initial?.config?.authType as string) ?? 'none');
  const [apiAuthUser, setApiAuthUser] = useState<string>((initial?.config?.authUser as string) ?? '');
  const [apiAuthPass, setApiAuthPass] = useState<string>((initial?.config?.authPass as string) ?? '');
  const [apiAuthToken, setApiAuthToken] = useState<string>((initial?.config?.authToken as string) ?? '');
  const [apiAuthHeaderName, setApiAuthHeaderName] = useState<string>((initial?.config?.authHeaderName as string) ?? '');
  const [apiAuthHeaderValue, setApiAuthHeaderValue] = useState<string>((initial?.config?.authHeaderValue as string) ?? '');

  useEffect(() => {
    if (initial) {
      setName(initial.name);
      setType(initial.type as any);
      setSpreadsheetId((initial.config?.spreadsheetId as string) ?? '');
      setCalendarId((initial.config?.calendarId as string) ?? 'primary');
      setApiUrl((initial.config?.url as string) ?? '');
      setApiMethod((initial.config?.method as string) ?? 'GET');
      setApiAuthType((initial.config?.authType as string) ?? 'none');
      setApiAuthUser((initial.config?.authUser as string) ?? '');
      setApiAuthPass((initial.config?.authPass as string) ?? '');
      setApiAuthToken((initial.config?.authToken as string) ?? '');
      setApiAuthHeaderName((initial.config?.authHeaderName as string) ?? '');
      setApiAuthHeaderValue((initial.config?.authHeaderValue as string) ?? '');
    } else {
      setName('');
      setType('google_sheets');
      setSpreadsheetId('');
      setCalendarId('primary');
      setApiUrl('');
      setApiMethod('GET');
      setApiAuthType('none');
      setApiAuthUser('');
      setApiAuthPass('');
      setApiAuthToken('');
      setApiAuthHeaderName('');
      setApiAuthHeaderValue('');
    }
    setSheetNames([]);
    setSheetError(null);
    setSheetOk(false);
  }, [initial, open]);

  const handleFetchSheets = async (id: string) => {
    if (!id.trim()) {
      setSheetNames([]);
      setSheetError(null);
      setSheetOk(false);
      return;
    }
    setSheetLoading(true);
    setSheetError(null);
    setSheetOk(false);
    try {
      const names = await fetchSheetNames(id);
      setSheetNames(names);
      setSheetOk(true);
    } catch (err: any) {
      setSheetError(err.message ?? 'Erro ao verificar planilha');
      setSheetNames([]);
    } finally {
      setSheetLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    let config: Record<string, any>;
    if (type === 'google_sheets') {
      config = { spreadsheetId: spreadsheetId.trim() };
    } else if (type === 'google_calendar') {
      config = { calendarId: calendarId.trim() || 'primary' };
    } else {
      config = {
        url: apiUrl.trim(),
        method: apiMethod,
        authType: apiAuthType,
        ...(apiAuthType === 'basic' && { authUser: apiAuthUser, authPass: apiAuthPass }),
        ...(apiAuthType === 'bearer' && { authToken: apiAuthToken }),
        ...(apiAuthType === 'header' && { authHeaderName: apiAuthHeaderName, authHeaderValue: apiAuthHeaderValue }),
      };
    }
    onSave({ name: name.trim(), type, config });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Integração' : 'Nova Integração'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize os dados da integração.'
              : 'Conecte um serviço externo ao workspace.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="int-name">Nome da integração</Label>
            <Input
              id="int-name"
              placeholder="Ex: Planilha de Vendas"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Type — locked when editing */}
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <div className="grid grid-cols-2 gap-3">
                {(['google_sheets', 'google_calendar'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      type === t
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{TYPE_ICONS[t]}</div>
                    <div className="font-medium text-sm">{TYPE_LABELS[t]}</div>
                  </button>
                ))}
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setType('rest_api')}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      type === 'rest_api'
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{TYPE_ICONS['rest_api']}</div>
                    <div className="font-medium text-sm">{TYPE_LABELS['rest_api']}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Super Admin</div>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Google Sheets config */}
          {type === 'google_sheets' && (
            <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                📊 Configuração do Google Sheets
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="sheet-id" className="text-xs">
                  URL ou ID da Planilha
                </Label>
                <Input
                  id="sheet-id"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={spreadsheetId}
                  onChange={(e) => {
                    setSpreadsheetId(e.target.value);
                    setSheetOk(false);
                    setSheetError(null);
                    setSheetNames([]);
                  }}
                  onBlur={(e) => e.target.value.trim() && handleFetchSheets(e.target.value)}
                  onPaste={(e) => {
                    setTimeout(() => {
                      const v = e.currentTarget.value;
                      if (v.trim()) handleFetchSheets(v);
                    }, 100);
                  }}
                  className="text-xs"
                />
                {sheetLoading && (
                  <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Verificando planilha...
                  </div>
                )}
                {sheetOk && !sheetLoading && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                    <Check className="w-3 h-3" />
                    Planilha encontrada — {sheetNames.length} abas disponíveis
                  </div>
                )}
                {sheetError && !sheetLoading && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                    <X className="w-3 h-3" />
                    {sheetError}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground space-y-1 pt-1">
                <p>⚠️ Compartilhe a planilha com a Service Account do servidor</p>
                <p>📝 A primeira linha será usada como cabeçalhos</p>
              </div>
            </div>
          )}

          {/* REST API config */}
          {type === 'rest_api' && (
            <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg">
              <p className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                🔌 Configuração da API REST
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="api-url" className="text-xs">URL da API</Label>
                <Input
                  id="api-url"
                  placeholder="https://api.exemplo.com/dados"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  className="text-xs"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="api-method" className="text-xs">Método</Label>
                <select
                  id="api-method"
                  value={apiMethod}
                  onChange={(e) => setApiMethod(e.target.value)}
                  className="w-full text-xs border border-input rounded-md px-3 py-2 bg-background"
                >
                  {['GET', 'POST', 'PUT', 'PATCH'].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="api-auth" className="text-xs">Autenticação</Label>
                <select
                  id="api-auth"
                  value={apiAuthType}
                  onChange={(e) => setApiAuthType(e.target.value)}
                  className="w-full text-xs border border-input rounded-md px-3 py-2 bg-background"
                >
                  <option value="none">Nenhuma</option>
                  <option value="basic">Basic Auth (usuário/senha)</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="header">Header customizado</option>
                </select>
              </div>
              {apiAuthType === 'basic' && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Usuário</Label>
                    <Input value={apiAuthUser} onChange={(e) => setApiAuthUser(e.target.value)} className="text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Senha</Label>
                    <Input type="password" value={apiAuthPass} onChange={(e) => setApiAuthPass(e.target.value)} className="text-xs" />
                  </div>
                </div>
              )}
              {apiAuthType === 'bearer' && (
                <div className="space-y-1">
                  <Label className="text-xs">Token</Label>
                  <Input value={apiAuthToken} onChange={(e) => setApiAuthToken(e.target.value)} className="text-xs font-mono" placeholder="eyJ..." />
                </div>
              )}
              {apiAuthType === 'header' && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome do header</Label>
                    <Input value={apiAuthHeaderName} onChange={(e) => setApiAuthHeaderName(e.target.value)} className="text-xs font-mono" placeholder="X-API-Key" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valor</Label>
                    <Input value={apiAuthHeaderValue} onChange={(e) => setApiAuthHeaderValue(e.target.value)} className="text-xs font-mono" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Google Calendar config */}
          {type === 'google_calendar' && (
            <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                📅 Configuração do Google Calendar
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="cal-id" className="text-xs">
                  ID do Calendário
                </Label>
                <Input
                  id="cal-id"
                  placeholder="primary"
                  value={calendarId}
                  onChange={(e) => setCalendarId(e.target.value)}
                  className="text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="bg-muted px-1 rounded">primary</code> para o calendário
                  principal ou o e-mail/ID de outro calendário.
                </p>
              </div>
              {serviceAccountEmail && (
                <div className="space-y-1 pt-1">
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                    Service Account — adicione este e-mail ao calendário:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded flex-1 break-all">
                      {serviceAccountEmail}
                    </code>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded bg-blue-200 dark:bg-blue-800 hover:bg-blue-300 dark:hover:bg-blue-700 transition-colors whitespace-nowrap"
                      onClick={() => navigator.clipboard.writeText(serviceAccountEmail)}
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              {isEdit ? 'Salvar' : 'Criar integração'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Settings Page ───────────────────────────────────────────────────────
export default function SettingsPage() {
  const { currentWorkspace } = useWorkspaceStore();
  const { setIntegrations, addIntegration, updateIntegration, removeIntegration } =
    useIntegrationsStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; message: string }>>({});

  // Load service account email for instructions
  useEffect(() => {
    fetchServiceAccountEmail().then(setServiceAccountEmail);
  }, []);

  // Fetch integrations
  const { data: integrations = [], isLoading } = useQuery({
    queryKey: ['integrations', currentWorkspace?.id],
    queryFn: () => integrationsApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  // Sync fetched integrations to the store
  useEffect(() => {
    if (integrations.length > 0) setIntegrations(integrations);
  }, [integrations, setIntegrations]);

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof integrationsApi.create>[1]) =>
      integrationsApi.create(currentWorkspace!.id, data),
    onSuccess: (created) => {
      addIntegration(created);
      queryClient.invalidateQueries({ queryKey: ['integrations', currentWorkspace?.id] });
      toast({ title: 'Integração criada com sucesso!' });
      setFormOpen(false);
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao criar integração',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof integrationsApi.update>[2] }) =>
      integrationsApi.update(currentWorkspace!.id, id, data),
    onSuccess: (updated) => {
      updateIntegration(updated);
      queryClient.invalidateQueries({ queryKey: ['integrations', currentWorkspace?.id] });
      toast({ title: 'Integração atualizada!' });
      setFormOpen(false);
      setEditingIntegration(null);
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao atualizar integração',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => integrationsApi.remove(currentWorkspace!.id, id),
    onSuccess: (_, id) => {
      removeIntegration(id);
      queryClient.invalidateQueries({ queryKey: ['integrations', currentWorkspace?.id] });
      toast({ title: 'Integração removida.' });
      setDeletingId(null);
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao remover integração',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const handleTest = async (integration: Integration) => {
    setTestingId(integration.id);
    try {
      if (integration.type === 'google_sheets') {
        const id = integration.config?.spreadsheetId as string;
        if (!id) throw new Error('Nenhum ID de planilha configurado');
        const names = await fetchSheetNames(id);
        setTestResults((prev) => ({
          ...prev,
          [integration.id]: { ok: true, message: `${names.length} aba(s) encontrada(s)` },
        }));
      } else if (integration.type === 'google_calendar') {
        const calId = (integration.config?.calendarId as string) || 'primary';
        const now = new Date();
        const timeMax = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
        const events = await fetchCalendarEvents(calId, now.toISOString(), timeMax);
        setTestResults((prev) => ({
          ...prev,
          [integration.id]: { ok: true, message: `Conexão OK — ${events.length} evento(s) nos próximos 7 dias` },
        }));
      } else if (integration.type === 'rest_api') {
        if (!integration.config?.url) throw new Error('Nenhuma URL configurada');
        await integrationsApi.fetchRestApi(currentWorkspace!.id, integration.id);
        setTestResults((prev) => ({
          ...prev,
          [integration.id]: { ok: true, message: 'Conexão OK — API respondeu com sucesso' },
        }));
      }
    } catch (err: any) {
      setTestResults((prev) => ({
        ...prev,
        [integration.id]: { ok: false, message: err.message ?? 'Erro ao testar integração' },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleSave = (data: { name: string; type: string; config: Record<string, any> }) => {
    if (editingIntegration) {
      updateMutation.mutate({
        id: editingIntegration.id,
        data: { name: data.name, config: data.config },
      });
    } else {
      createMutation.mutate(data as any);
    }
  };

  const { user } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const grouped = {
    google_sheets: integrations.filter((i) => i.type === 'google_sheets'),
    google_calendar: integrations.filter((i) => i.type === 'google_calendar'),
    rest_api: integrations.filter((i) => i.type === 'rest_api'),
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Configurações</h1>
        <p className="text-muted-foreground text-sm mt-1">Gerencie as integrações do workspace</p>
      </div>

      {/* ── Integrations section ────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Integrações</h2>
            <p className="text-sm text-muted-foreground">
              Conecte serviços externos e reutilize-os em qualquer componente do workspace.
            </p>
          </div>
          <Button
            onClick={() => {
              setEditingIntegration(null);
              setFormOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova integração
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Carregando integrações...
          </div>
        ) : integrations.length === 0 ? (
          <div className="border border-dashed rounded-xl py-16 flex flex-col items-center gap-3 text-muted-foreground">
            <div className="text-4xl">🔌</div>
            <p className="font-medium">Nenhuma integração configurada</p>
            <p className="text-sm text-center max-w-xs">
              Crie integrações com Google Sheets ou Google Calendar e depois selecione-as nos
              componentes do dashboard.
            </p>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => {
                setEditingIntegration(null);
                setFormOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar primeira integração
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {(
              [
                { key: 'google_sheets', label: 'Google Sheets' },
                { key: 'google_calendar', label: 'Google Calendar' },
                ...(isSuperAdmin ? [{ key: 'rest_api', label: 'API REST' }] : []),
              ] as { key: 'google_sheets' | 'google_calendar' | 'rest_api'; label: string }[]
            ).map(({ key, label }) =>
              grouped[key].length === 0 ? null : (
                <div key={key}>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                    {TYPE_ICONS[key]} {label}
                  </h3>
                  <div className="space-y-2">
                    {grouped[key].map((integration) => (
                      <div
                        key={integration.id}
                        className="flex items-center justify-between p-4 border rounded-xl bg-card hover:bg-accent/30 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{integration.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {key === 'google_sheets'
                              ? integration.config?.spreadsheetId || '—'
                              : key === 'google_calendar'
                              ? `Calendário: ${integration.config?.calendarId || 'primary'}`
                              : integration.config?.url || '—'}
                          </p>
                          {testResults[integration.id] && (
                            <p className={`text-xs mt-1 flex items-center gap-1 ${
                              testResults[integration.id].ok
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {testResults[integration.id].ok
                                ? <Check className="w-3 h-3" />
                                : <AlertCircle className="w-3 h-3" />}
                              {testResults[integration.id].message}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            title="Testar integração"
                            disabled={testingId === integration.id}
                            onClick={() => handleTest(integration)}
                          >
                            {testingId === integration.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Play className="w-3.5 h-3.5" />}
                          </Button>
                          {key === 'rest_api' && integration.config?.url && (
                            <a
                              href={integration.config.url as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
                              title="Abrir URL"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {key === 'google_sheets' && integration.config?.spreadsheetId && (
                            <a
                              href={`https://docs.google.com/spreadsheets/d/${integration.config.spreadsheetId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
                              title="Abrir planilha"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingIntegration(integration);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeletingId(integration.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </section>

      {/* ── Service Account info ─────────────────────────────────────────────── */}
      {serviceAccountEmail && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Service Account</h2>
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-sm space-y-2">
            <p className="font-medium text-amber-800 dark:text-amber-300">
              📧 E-mail da Service Account configurada no servidor
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Para usar Google Sheets ou Google Calendar, compartilhe o recurso com este e-mail:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-amber-100 dark:bg-amber-900 px-2 py-1.5 rounded break-all">
                {serviceAccountEmail}
              </code>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded bg-amber-200 dark:bg-amber-800 hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors whitespace-nowrap"
                onClick={() => navigator.clipboard.writeText(serviceAccountEmail)}
              >
                Copiar
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Create / Edit modal ──────────────────────────────────────────────── */}
      <IntegrationFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingIntegration(null);
        }}
        onSave={handleSave}
        initial={editingIntegration}
        serviceAccountEmail={serviceAccountEmail}
        isSuperAdmin={isSuperAdmin}
      />

      {/* ── Delete confirm dialog ────────────────────────────────────────────── */}
      <Dialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remover integração?</DialogTitle>
            <DialogDescription>
              Componentes que usam esta integração deixarão de sincronizar dados. Esta ação não pode
              ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Removendo...' : 'Remover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
